import { useState, useEffect } from 'react';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import { appService } from '@/services/AppService';
import { useToast } from '@/hooks/use-toast';
import { isSystemApp, isAppStoreApp, isHiddenFromStore } from '@/lib/apps';

export interface App {
  id: string;
  name: string;
  description?: string;
  icon_name: string;
  component_type: string;
  category: string;
  version: string;
  developer_id?: string;
  is_published: boolean;
  rating?: number;
  download_count: number;
  created_at: string;
  updated_at: string;
  metadata?: any;
}

export interface OrganizationInstalledApp {
  id: string;
  organization_id: string;
  app_id: string;
  installed_by: string;
  is_in_dock: boolean;
  dock_position?: number;
  app_config?: any;
  installed_at: string;
  last_used_at?: string;
}

export function useOrganizationAppStore() {
  const [apps, setApps] = useState<App[]>([]);
  const [organizationInstalledApps, setOrganizationInstalledApps] = useState<OrganizationInstalledApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [appsLoaded, setAppsLoaded] = useState(false);
  const [addingToDockIds, setAddingToDockIds] = useState<Set<string>>(new Set());
  const [installationProgress, setInstallationProgress] = useState<Map<string, number>>(new Map());
  const { currentOrganizationId, userProfileId, loading: orgContextLoading } = useOrganizationContext();
  const { toast } = useToast();

  // Load published apps
  useEffect(() => {
    const loadApps = async () => {
      try {
        console.log('🔄 Loading published apps...');
        const fetchedApps = await appService.getPublishedApps();
        console.log('📱 Fetched apps:', fetchedApps?.length || 0, 'apps');
        
        // Filter out apps that should be hidden from the store
        const visibleApps = fetchedApps?.filter(app => !isHiddenFromStore(app)) || [];
        const hiddenCount = (fetchedApps?.length || 0) - visibleApps.length;
        
        if (hiddenCount > 0) {
          console.log('👁️ Hiding', hiddenCount, 'apps from store display');
        }
        
        setApps(visibleApps);
        setAppsLoaded(true);
        
        // Log App Store detection
        const appStore = fetchedApps?.find(app => app.component_type === 'AppStoreWindow' && app.is_published);
        if (appStore) {
          console.log('🏪 App Store found in published apps:', appStore.name, appStore.id);
        } else {
          console.warn('❌ App Store not found in published apps');
        }
      } catch (error) {
        console.error('❌ Error loading apps:', error);
        toast({
          title: "Error loading apps",
          description: "Failed to load available applications",
          variant: "destructive"
        });
        setAppsLoaded(true); // Still mark as loaded to avoid blocking
      }
    };

    loadApps();
  }, [toast]);

  // Load organization's installed apps with enhanced App Store handling
  useEffect(() => {
    const loadOrganizationApps = async () => {
      if (orgContextLoading) {
        console.log('⏳ Organization context still loading...');
        return;
      }

      if (!currentOrganizationId) {
        console.log('⚠️ Missing organization id, skipping load');
        setOrganizationInstalledApps([]);
        setLoading(false);
        return;
      }

      if (!userProfileId) {
        console.log('⏳ Waiting for user profile id before loading org apps...');
        return;
      }

      // Wait for apps to be loaded first to avoid race conditions
      if (!appsLoaded) {
        console.log('⏳ Waiting for apps to load before fetching org apps...');
        return;
      }

      console.log('🔄 Loading organization apps for:', currentOrganizationId);
      
      try {
        const installedApps = await appService.getOrganizationInstalledApps(currentOrganizationId);
        console.log('📊 Raw org apps response:', installedApps);
        console.log('📊 Organization installed apps count:', installedApps?.length || 0);
        
        setOrganizationInstalledApps(installedApps || []);

        // Enhanced App Store auto-install logic
        const appStore = apps.find(app => app.component_type === 'AppStoreWindow' && app.is_published);
        
        if (appStore) {
          console.log('🏪 App Store found:', appStore.name, appStore.id);
          const isAppStoreInstalled = installedApps?.some(ia => ia.app_id === appStore.id);
          console.log('🔍 App Store installed?', isAppStoreInstalled);
          
          if (!isAppStoreInstalled) {
            console.log('🚀 Auto-installing App Store for organization:', currentOrganizationId);
            try {
              const installSuccess = await appService.installAppForOrganization(appStore.id, currentOrganizationId, userProfileId);
              console.log('📦 App Store install result:', installSuccess);
              
              if (installSuccess) {
                const dockSuccess = await appService.addAppToDock(appStore.id, currentOrganizationId);
                console.log('📌 App Store dock result:', dockSuccess);
                
                // Re-fetch after auto-install
                const updatedApps = await appService.getOrganizationInstalledApps(currentOrganizationId);
                setOrganizationInstalledApps(updatedApps || []);
                console.log('✅ App Store auto-installed successfully');
              }
            } catch (autoInstallError) {
              console.error('❌ Failed to auto-install App Store:', autoInstallError);
            }
          }
        } else {
          console.warn('❌ App Store not found in apps array');
        }
      } catch (error) {
        console.error('❌ Error loading organization apps:', error);
        toast({
          title: "Error loading organization apps", 
          description: "Failed to load organization applications",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    loadOrganizationApps();
  }, [currentOrganizationId, userProfileId, appsLoaded, apps, toast, orgContextLoading]);

  const isAppInstalled = (appId: string): boolean => {
    // Enhanced App Store detection with system app fallback
    const isInstalled = organizationInstalledApps.some(installedApp => installedApp.app_id === appId);
    
    // Special handling for system apps - always consider them installed if published
    const app = apps.find(a => a.id === appId);
    if (app && isSystemApp(app) && app.is_published) {
      return true; // System apps are always "installed"
    }
    
    return isInstalled;
  };

  const isAppInDock = (appId: string): boolean => {
    // Check actual database state
    const isInDock = organizationInstalledApps.some(installedApp => 
      installedApp.app_id === appId && installedApp.is_in_dock
    );
    
    return isInDock;
  };

  const installApp = async (appId: string, appName: string): Promise<boolean> => {
    if (!currentOrganizationId || !userProfileId) {
      toast({
        title: "Error",
        description: "No organization selected",
        variant: "destructive"
      });
      return false;
    }

    try {
      console.log('📦 Installing app:', appName, appId);
      
      // Initialize progress at 0%
      setInstallationProgress(prev => new Map(prev).set(appId, 0));
      
      // Simulate progressive installation with smooth progress updates
      const progressSteps = [
        { progress: 15, delay: 200 },
        { progress: 35, delay: 300 },
        { progress: 60, delay: 400 },
        { progress: 85, delay: 350 },
      ];
      
      // Start progress simulation
      const progressPromises = progressSteps.map((step, index) => 
        new Promise<void>(resolve => {
          const totalDelay = progressSteps.slice(0, index + 1).reduce((sum, s) => sum + s.delay, 0);
          setTimeout(() => {
            setInstallationProgress(prev => new Map(prev).set(appId, step.progress));
            resolve();
          }, totalDelay);
        })
      );
      
      // Run actual installation alongside progress animation
      const success = await appService.installAppForOrganization(
        appId, 
        currentOrganizationId, 
        userProfileId
      );
      
      // Wait for at least the progress animation to complete
      await Promise.all(progressPromises);

      if (success) {
        // Set to 100% before finishing
        setInstallationProgress(prev => new Map(prev).set(appId, 100));
        
        // Small delay to show 100% completion
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Refresh installed apps
        let installedApps = await appService.getOrganizationInstalledApps(currentOrganizationId);
        setOrganizationInstalledApps(installedApps || []);
        
        // Check if app is in dock after install
        const installedApp = installedApps?.find(ia => ia.app_id === appId);
        if (installedApp && !installedApp.is_in_dock) {
          console.log('📌 App not in dock after install, adding to dock:', appName);
          await appService.addAppToDock(appId, currentOrganizationId);
          
          // Refresh again after adding to dock
          installedApps = await appService.getOrganizationInstalledApps(currentOrganizationId);
          setOrganizationInstalledApps(installedApps || []);
        }
        
        // Clear progress after successful installation
        setInstallationProgress(prev => {
          const newMap = new Map(prev);
          newMap.delete(appId);
          return newMap;
        });
        
        toast({
          title: "App installed",
          description: `${appName} has been installed successfully`,
        });
        return true;
      } else {
        // Clear progress on failure
        setInstallationProgress(prev => {
          const newMap = new Map(prev);
          newMap.delete(appId);
          return newMap;
        });
        
        toast({
          title: "Installation failed",
          description: `Failed to install ${appName}`,
          variant: "destructive"
        });
        return false;
      }
    } catch (error) {
      console.error('Error installing app:', error);
      
      // Clear progress on error
      setInstallationProgress(prev => {
        const newMap = new Map(prev);
        newMap.delete(appId);
        return newMap;
      });
      
      toast({
        title: "Installation error",
        description: `Error installing ${appName}`,
        variant: "destructive"
      });
      return false;
    }
  };

  const uninstallApp = async (appId: string, appName: string): Promise<boolean> => {
    if (!currentOrganizationId) {
      toast({
        title: "Error",
        description: "No organization selected",
        variant: "destructive"
      });
      return false;
    }

    // Check if it's a system app that shouldn't be uninstalled
    const app = apps.find(a => a.id === appId);
    if (app && isSystemApp(app)) {
      toast({
        title: "Cannot uninstall",
        description: "System apps cannot be uninstalled",
        variant: "destructive"
      });
      return false;
    }

    try {
      const success = await appService.uninstallAppForOrganization(appId, currentOrganizationId);

      if (success) {
        // Refresh installed apps
        const installedApps = await appService.getOrganizationInstalledApps(currentOrganizationId);
        setOrganizationInstalledApps(installedApps || []);
        
        toast({
          title: "App uninstalled",
          description: `${appName} has been uninstalled`,
        });
        return true;
      } else {
        toast({
          title: "Uninstall failed",
          description: `Failed to uninstall ${appName}`,
          variant: "destructive"
        });
        return false;
      }
    } catch (error) {
      console.error('Error uninstalling app:', error);
      toast({
        title: "Uninstall error",
        description: `Error uninstalling ${appName}`,
        variant: "destructive"
      });
      return false;
    }
  };

  const addToDock = async (appId: string, appName: string): Promise<boolean> => {
    if (!currentOrganizationId) {
      toast({
        title: "Error",
        description: "No organization selected",
        variant: "destructive"
      });
      return false;
    }

    // Prevent duplicate requests
    if (addingToDockIds.has(appId)) {
      return false;
    }

    // Mark as loading
    setAddingToDockIds(prev => new Set(prev).add(appId));

    // Optimistic update - immediately show in dock
    setOrganizationInstalledApps(prev => {
      const existingApp = prev.find(ia => ia.app_id === appId);
      if (existingApp) {
        // App is installed, just update the is_in_dock flag
        return prev.map(ia => 
          ia.app_id === appId 
            ? { ...ia, is_in_dock: true }
            : ia
        );
      }
      // If app isn't installed yet (shouldn't happen for add-to-dock), return unchanged
      return prev;
    });

    try {
      const success = await appService.addAppToDock(appId, currentOrganizationId);

      if (success) {
        // Refresh installed apps from server to ensure consistency
        const installedApps = await appService.getOrganizationInstalledApps(currentOrganizationId);
        setOrganizationInstalledApps(installedApps || []);
        
        // Show success toast briefly
        toast({
          title: "Added to dock",
          description: `${appName} has been added`,
          duration: 2000, // Short duration for seamless feel
        });
        return true;
      } else {
        // Revert optimistic update on failure
        setOrganizationInstalledApps(prev => {
          return prev.map(ia => 
            ia.app_id === appId 
              ? { ...ia, is_in_dock: false }
              : ia
          );
        });
        
        toast({
          title: "Failed to add to dock",
          description: `Failed to add ${appName} to dock`,
          variant: "destructive"
        });
        return false;
      }
    } catch (error) {
      console.error('Error adding to dock:', error);
      
      // Revert optimistic update on error
      setOrganizationInstalledApps(prev => {
        return prev.map(ia => 
          ia.app_id === appId 
            ? { ...ia, is_in_dock: false }
            : ia
        );
      });
      
      toast({
        title: "Dock error",
        description: `Failed to add ${appName} to dock`,
        variant: "destructive"
      });
      return false;
    } finally {
      // Remove from loading state
      setAddingToDockIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(appId);
        return newSet;
      });
    }
  };

  const isAddingToDock = (appId: string): boolean => {
    return addingToDockIds.has(appId);
  };

  const removeFromDock = async (appId: string, appName: string): Promise<boolean> => {
    console.log('🎣 useOrganizationAppStore.removeFromDock called:', appId, appName);
    
    if (!currentOrganizationId) {
      console.error('❌ No organization selected');
      toast({
        title: "Error",
        description: "No organization selected",
        variant: "destructive"
      });
      return false;
    }
    
    console.log('🏛️ Current organization:', currentOrganizationId);

    // Only block App Store removal from dock; allow all other system apps to be removed
    const app = apps.find(a => a.id === appId);
    if (app && isAppStoreApp(app)) {
      console.log('⛔ App Store cannot be removed from dock:', appName);
      toast({
        title: "Cannot remove App Store",
        description: "The App Store cannot be removed from the dock",
        variant: "destructive"
      });
      return false;
    }

    try {
      console.log('🚀 Calling appService.removeAppFromDock...');
      const success = await appService.removeAppFromDock(appId, currentOrganizationId);
      console.log('📦 appService.removeAppFromDock returned:', success);

      if (success) {
        console.log('✅ Removal successful, refreshing installed apps...');
        // Refresh installed apps
        const installedApps = await appService.getOrganizationInstalledApps(currentOrganizationId);
        console.log('📋 Refreshed installed apps:', installedApps?.length || 0);
        setOrganizationInstalledApps(installedApps || []);
        
        toast({
          title: "Removed from dock",
          description: `${appName} has been removed from the dock`,
        });
        return true;
      } else {
        console.error('❌ Removal failed');
        toast({
          title: "Failed to remove from dock",
          description: `Failed to remove ${appName} from dock`,
          variant: "destructive"
        });
        return false;
      }
    } catch (error) {
      console.error('🔥 Error removing from dock:', error);
      toast({
        title: "Dock error",
        description: `Error removing ${appName} from dock`,
        variant: "destructive"
      });
      return false;
    }
  };

  const updateLastUsed = async (appId: string): Promise<void> => {
    if (!currentOrganizationId) return;

    try {
      await appService.updateAppLastUsed(appId, currentOrganizationId);
      // Refresh installed apps
      const installedApps = await appService.getOrganizationInstalledApps(currentOrganizationId);
      setOrganizationInstalledApps(installedApps || []);
    } catch (error) {
      console.error('Error updating last used:', error);
    }
  };

  return {
    apps,
    organizationInstalledApps,
    loading,
    currentOrganizationId,
    isAppInstalled,
    isAppInDock,
    isAddingToDock,
    installApp,
    uninstallApp,
    addToDock,
    removeFromDock,
    updateLastUsed,
    installationProgress
  };
}
